import * as React from 'react';
import { useDataProvider } from 'react-admin';
import { useController } from 'react-hook-form';
import { Box, CircularProgress, Typography } from '@mui/material';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import type { RJSFSchema, RegistryWidgetsType, UiSchema } from '@rjsf/utils';
import type { IChangeEvent } from '@rjsf/core';

interface InfoSchemaFormProps {
    resource: string; // e.g. 'api-config' or 'oauth-client'
    type: string; // e.g. 'openid', 'apikey', 'basicauth'
    source?: string; // form field name, defaults to 'info'
    include?: string[]; // render only these top-level properties
    exclude?: string[]; // render all top-level properties except these
    widgets?: RegistryWidgetsType; // custom RJSF widgets
    uiSchema?: UiSchema; // extra ui:schema merged into the defaults
}

// Top-level info properties that belong to the Basic Auth section,
// depending on the client type. Shared by the oauth-client edit/create forms.
export const getOAuthBasicAuthFields = (type: string): string[] =>
    type === 'apikey'
        ? [
              'allow_basic_auth',
              'client_id',
              'client_secret',
              'user_guid',
              'allowed_apis',
              'access_policy',
          ]
        : ['allow_basic_auth', 'user_guid', 'allowed_apis', 'access_policy'];

const filterSchema = (
    schema: RJSFSchema,
    include?: string[],
    exclude?: string[]
): RJSFSchema => {
    const properties = schema?.properties as
        | Record<string, unknown>
        | undefined;
    if (!properties || (!include && !exclude)) return schema;
    const keys = Object.keys(properties).filter(key => {
        if (include) return include.includes(key);
        if (exclude) return !exclude.includes(key);
        return true;
    });
    const filteredProps: Record<string, unknown> = {};
    keys.forEach(key => {
        filteredProps[key] = properties[key];
    });
    const required = Array.isArray(schema.required)
        ? schema.required.filter((key: string) => keys.includes(key))
        : undefined;
    return {
        ...schema,
        properties: filteredProps,
        ...(required ? { required } : {}),
    } as RJSFSchema;
};

const InfoSchemaForm = ({
    resource,
    type,
    source = 'info',
    include,
    exclude,
    widgets,
    uiSchema,
}: InfoSchemaFormProps) => {
    const dataProvider = useDataProvider();
    const [schema, setSchema] = React.useState<RJSFSchema | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const schemaRef = React.useRef<RJSFSchema | null>(null);

    const validateAgainstSchema = React.useCallback((value: any) => {
        const s = schemaRef.current;
        if (!s) return true;
        const result = validator.validateFormData(value || {}, s);
        if (result.errors && result.errors.length > 0) {
            return result.errors
                .map((e: any) => e.message || e.stack)
                .join('; ');
        }
        return true;
    }, []);

    const { field } = useController({
        name: source,
        rules: { validate: validateAgainstSchema },
    });

    React.useEffect(() => {
        schemaRef.current = schema;
    }, [schema]);

    React.useEffect(() => {
        if (!type) {
            setSchema(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        (dataProvider as any)
            .getInfoSchema(resource, type)
            .then(({ data }: { data: any }) => {
                setSchema(data);
                setLoading(false);
            })
            .catch((err: any) => {
                setError(err?.message || 'Failed to load schema');
                setLoading(false);
            });
    }, [dataProvider, resource, type]);

    const handleChange = (e: IChangeEvent) => {
        // Merge into the existing value so that sibling blocks bound to the
        // same source (rendering a different subset of fields) are preserved.
        field.onChange({ ...(field.value || {}), ...e.formData });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading schema...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Typography variant="body2" color="error" sx={{ py: 1 }}>
                {error}
            </Typography>
        );
    }

    if (!schema) {
        return null;
    }

    // Remove top-level title — the section header is rendered separately
    const { title, ...schemaWithoutTitle } = schema;

    // Render only the requested subset of fields (if include/exclude given).
    // Validation in the controller still runs against the full schema.
    const renderSchema = filterSchema(
        schemaWithoutTitle as RJSFSchema,
        include,
        exclude
    );

    return (
        <Box sx={{ mt: 0 }}>
            <Form
                schema={renderSchema}
                formData={field.value || {}}
                onChange={handleChange}
                validator={validator}
                widgets={widgets}
                uiSchema={{
                    'ui:submitButtonOptions': { norender: true },
                    type: { 'ui:widget': 'hidden' },
                    ...uiSchema,
                }}
                liveValidate
            />
        </Box>
    );
};

export default InfoSchemaForm;
